import React, { useState, useEffect, useMemo } from 'react';
import { objectivesApi, programmesApi, kpisApi, businessPlansApi, clustersApi, risksApi, projectsApi, strategicAlignmentApi, divisionsApi } from '../services/api';
import { Target, GitBranch, BarChart3, FileText, Link2, ChevronDown, ChevronRight, FolderKanban, ShieldAlert, Plus, X, AlertTriangle, CheckCircle2, Sliders, Edit3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ═══════════════════════════════════════════════════
// STRATEGY & BUSINESS PLANNING MODULE
// Cascade-inspired: Planner | Alignment | Metrics | Strategic Alignment | Business Plans
// ═══════════════════════════════════════════════════

// ── Helpers ──
const pc = p => p >= 80 ? '#10b981' : p >= 50 ? '#3b82f6' : p >= 30 ? '#f59e0b' : '#ef4444';
const statusLabel = p => p >= 80 ? 'Achieved' : p >= 50 ? 'On Track' : p >= 30 ? 'Behind' : 'At Risk';
const statusBadge = p => {
  const l = statusLabel(p);
  const c = l === 'Achieved' ? 'bg-emerald-100 text-emerald-700' : l === 'On Track' ? 'bg-blue-100 text-blue-700' : l === 'Behind' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${c}`}>{l === 'Achieved' ? '' : l === 'On Track' ? '↗' : l === 'Behind' ? '↘' : '↓'} {l}</span>;
};
const fmtR = v => v ? `R${(v/1000000).toFixed(1)}M` : '—';
const STAGES = {Draft:'Draft',InProgress:'In Progress',Active:'Under Review',OnHold:'Submitted',Completed:'Discussed',Closed:'Approved'};
const stageColor = s => ({Draft:'#94a3b8',InProgress:'#3b82f6',Active:'#f59e0b',OnHold:'#8b5cf6',Completed:'#10b981',Closed:'#059669'}[s]||'#94a3b8');
const input = "w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e] transition-colors";
const burgundy = '#970c24';
const softPanel = 'bg-white/95 rounded-xl border border-slate-200 shadow-[0_8px_24px_rgba(15,23,42,0.08)]';
const tinyHeading = 'text-[11px] font-bold uppercase tracking-[0.08em] text-slate-700';

// ── Strategic Alignment helpers (V3.7) ──
const vColor = v => Math.abs(v) <= 1.5 ? '#10b981' : v > 0 ? '#f59e0b' : '#ef4444';
const aColor = s => s >= 90 ? '#10b981' : s >= 85 ? '#f59e0b' : '#ef4444';
const aBand = s => s >= 90 ? 'Strong' : s >= 85 ? 'Moderate' : 'Weak';
// Heatmap cell — burgundy-scaled to match the page brand
const cellStyle = v => {
  const i = Math.min(0.95, Math.max(0.08, v / 32));
  return { background: `rgba(151, 12, 36, ${i})`, color: i > 0.55 ? '#fff' : '#1e293b' };
};
const EMPTY_ALIGNMENT = {
  summary: { divisionCount: 0, objectiveCount: 0, averageAlignment: 0, largestVarianceValue: 0, largestVarianceObjective: '—', divisionsWithCompletePlans: 0, companyTargetSumsTo: 0 },
  objectives: [],
  matrix: []
};

// ── Modal ──
function Modal({open, onClose, title, width, children}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center pt-[8vh] overflow-y-auto" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl ${width||'max-w-2xl'} w-full mx-4 mb-8`} onClick={e=>e.stopPropagation()} style={{animation:'slideUp .25s ease-out'}}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-800">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"><X size={16}/></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// TAB 1: PLANNER (redesigned to match the supplied planner screenshot)
// ════════════════════════════════════════
function PlannerTab({ objectives, allProjects = [], fy = 2026, onRefresh }) {
  const [expanded, setExpanded] = useState({});
  const [expandedInit, setExpandedInit] = useState({});
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const toggle = id => setExpanded(p => ({...p, [id]: !p[id]}));
  const toggleInit = id => setExpandedInit(p => ({...p, [id]: !p[id]}));

  const safePct = v => Math.max(0, Math.min(100, Number(v || 0)));
  const softStatusPill = p => {
    const label = statusLabel(p);
    const styles = label === 'Achieved'
      ? 'bg-slate-800 text-white'
      : label === 'On Track'
      ? 'bg-slate-600 text-white'
      : label === 'Behind'
      ? 'bg-slate-700 text-white'
      : 'bg-[#970c24] text-white';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${styles}`}>{label}</span>;
  };

  // Get projects for a programme — from both old FK and new junction
  const getProgProjects = (progId) => {
    try {
      const fromFK = (objectives.flatMap(o => o.initiatives || []).find(i => i.id === progId)?.projects) || [];
      const fromJunction = (allProjects || []).filter(p => {
        try { return (p.programmes || []).some(x => x.initiativeId === progId); } catch { return false; }
      });
      const seen = new Set(fromFK.map(p => p.id));
      return [...fromFK, ...fromJunction.filter(p => !seen.has(p.id))];
    } catch { return []; }
  };

  // Get ALL projects linked to an SO — from programmes + direct junction
  const getSOProjects = (soId) => {
    try {
      const fromProgrammes = (objectives.find(o => o.id === soId)?.initiatives || []).flatMap(i => i.projects || []);
      const fromJunction = (allProjects || []).filter(p => {
        try { return (p.strategicObjectives || []).some(x => x.strategicObjectiveId === soId); } catch { return false; }
      });
      const seen = new Set(fromProgrammes.map(p => p.id));
      return [...fromProgrammes, ...fromJunction.filter(p => !seen.has(p.id))];
    } catch { return []; }
  };

  // Plan Health calculation
  const total = objectives.length || 1;
  const atRisk = objectives.filter(o => o.progressPercentage < 30).length;
  const behind = objectives.filter(o => o.progressPercentage >= 30 && o.progressPercentage < 50).length;
  const onTrack = objectives.filter(o => o.progressPercentage >= 50 && o.progressPercentage < 80).length;
  const achieved = objectives.filter(o => o.progressPercentage >= 80).length;

  const allKpis = objectives.flatMap(o => o.kpis || []);
  const allInitiatives = objectives.flatMap(o => o.initiatives || []);
  const allRisks = objectives.flatMap(o => o.risks || []);

  // Add SO
  const openAddSO = () => { setForm({title:'',description:'',sortOrder:objectives.length+1,financialYear:fy,progressPercentage:0}); setModal('addSO'); };
  const saveSO = async () => { try { await objectivesApi.create(form); setModal(null); onRefresh(); } catch(e) { alert('Error'); }};

  // Add Programme
  const openAddInit = soId => { setForm({title:'',description:'',strategicObjectiveId:soId,progressPercentage:0}); setModal('addInit'); };
  const saveInit = async () => { try { await programmesApi.create(form); setModal(null); onRefresh(); } catch(e) { alert('Error'); }};

  const healthCards = [
    { label:'At Risk', count:atRisk, pct:Math.round((atRisk/total)*100), goals:`${atRisk}/${total} Goals`, icon:'↓', cap:'bg-[#970c24]', accent:'text-[#970c24]' },
    { label:'Behind', count:behind, pct:Math.round((behind/total)*100), goals:`${behind}/${total} Goals`, icon:'↘', cap:'bg-[#202427]', accent:'text-[#202427]' },
    { label:'On Track', count:onTrack, pct:Math.round((onTrack/total)*100), goals:`${onTrack}/${total} Goals`, icon:'↗', cap:'bg-slate-400', accent:'text-slate-700' },
    { label:'Achieved', count:achieved, pct:Math.round((achieved/total)*100), goals:`${achieved}/${total} Goals`, icon:'▣', cap:'bg-[#202427]', accent:'text-[#202427]' },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-[#dededb] border border-[#d1d1ce] shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2">
          <h3 className="text-[10px] font-black uppercase tracking-[0.08em] text-slate-800">Plan Health Performance</h3>
          <select className="h-7 text-[10px] border border-slate-300 rounded bg-[#eeeeec] px-2 text-slate-600 outline-none">
            <option>FY 2025/26</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 px-3 pb-3">
          {healthCards.map(s => (
            <div key={s.label} className="rounded-md border border-[#c9c9c6] bg-[#f7f7f5] shadow-[0_1px_4px_rgba(15,23,42,0.12)] overflow-hidden">
              <div className={`h-8 ${s.cap}`} />
              <div className="px-4 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`${s.accent} text-[11px] font-bold`}>{s.icon}</span>
                  <span className={`${s.accent} text-[11px] font-black uppercase tracking-wide`}>{s.label}</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className={`${s.accent} text-[30px] leading-none font-black tracking-tight`}>{s.pct}%</span>
                  <span className="text-[10px] text-slate-700 mb-1">{s.goals}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-5 px-3 py-2 border-t border-[#cfcfcb] text-[10px] text-slate-700">
          <span><b className="text-slate-900">{objectives.length}</b> Strategic Objectives</span>
          <span><b className="text-slate-900">{allInitiatives.length}</b> Programmes</span>
          <span><b className="text-slate-900">{allKpis.length}</b> KPI Measures</span>
          <span><b className="text-slate-900">{allRisks.length}</b> Linked Risks</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <h3 className="text-[10px] font-black uppercase tracking-[0.08em] text-slate-800">Strategic Objectives Hierarchy</h3>
        <button onClick={openAddSO} className="px-2.5 py-1 bg-[#1f2327] text-white rounded text-[9px] font-bold hover:bg-[#970c24] transition-colors flex items-center gap-1 shadow-sm"><Plus size={11}/> Add Objective</button>
      </div>

      <div className="space-y-1.5">
        {objectives.map((so, idx) => {
          const soOpen = expanded[so.id] ?? idx === 0;
          const pct = safePct(so.progressPercentage);
          return (
          <div key={so.id} className="rounded-md border border-[#d2d2cf] bg-[#f8f8f6] shadow-[0_1px_3px_rgba(15,23,42,0.12)] overflow-hidden">
            <div className="grid grid-cols-[24px_1fr_70px_70px_118px] items-center gap-2 px-2.5 py-2 cursor-pointer hover:bg-white/70" onClick={() => toggle(so.id)}>
              <div className="w-5 h-5 flex items-center justify-center text-slate-600">{soOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}</div>
              <div className="min-w-0 border-l-[3px] pl-2" style={{borderColor: pc(pct)}}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[8px] font-mono font-black text-[#970c24] uppercase">{so.reference}</span>
                  <span className="text-[8px] text-slate-500 uppercase tracking-wide">Strategic Objective</span>
                </div>
                <p className="text-[11px] leading-tight font-black text-slate-900 truncate">{so.title}</p>
              </div>
              <div className="justify-self-end">{softStatusPill(pct)}</div>
              <div className="text-right">
                <p className="text-[12px] leading-none font-black" style={{color: pc(pct)}}>{pct}%</p>
                <p className="text-[8px] text-slate-500">complete</p>
              </div>
              <div className="h-1.5 rounded-full bg-[#e4e4e1] overflow-hidden">
                <div className="h-full rounded-full" style={{width:`${pct}%`, backgroundColor: pc(pct)}} />
              </div>
            </div>

            {soOpen && (
              <div className="border-t border-[#dededb] bg-white">
                {so.description && <p className="text-[10px] text-slate-600 px-8 pt-2 pb-1">{so.description}</p>}

                {(so.initiatives || []).map(prog => {
                  const progOpen = expandedInit[prog.id] ?? idx === 0;
                  const progPct = safePct(prog.progressPercentage);
                  return (
                  <div key={prog.id} className="border-t border-[#eeeeeb]">
                    <div className="grid grid-cols-[24px_1fr_70px_52px] items-center gap-2 px-2.5 py-2 pl-8 cursor-pointer hover:bg-[#f6f6f4]" onClick={() => toggleInit(prog.id)}>
                      <div className="w-5 h-5 flex items-center justify-center text-slate-500">{progOpen ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}</div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5"><span className="text-[8px] font-mono text-[#970c24] uppercase">{prog.reference}</span><span className="text-[8px] text-slate-500 uppercase tracking-wide">Programme</span></div>
                        <p className="text-[10px] leading-tight text-slate-800 truncate">{prog.title}</p>
                      </div>
                      <div className="justify-self-end">{softStatusPill(progPct)}</div>
                      <span className="text-[11px] font-black text-right" style={{color: pc(progPct)}}>{progPct}%</span>
                    </div>

                    {progOpen && getProgProjects(prog.id).map(prj => {
                      const prjPct = safePct(prj.progressPercentage || 0);
                      return (
                      <div key={prj.id} className="grid grid-cols-[16px_70px_1fr_72px_42px] items-center gap-2 px-3 py-1.5 pl-16 border-t border-[#f0f0ed] hover:bg-[#f8f8f6]">
                        <FolderKanban size={12} className="text-slate-400"/>
                        <span className="text-[8px] font-mono text-slate-600 uppercase truncate">{prj.reference}</span>
                        <span className="text-[10px] text-slate-700 truncate">{prj.title}</span>
                        <span className="justify-self-end px-2 py-0.5 rounded-full text-[8px] font-bold bg-slate-100 text-slate-600 truncate max-w-[72px]">{prj.status}</span>
                        <span className="text-[10px] font-black text-right" style={{color: pc(prjPct)}}>{prjPct}%</span>
                      </div>
                    );})}
                  </div>
                );})}

                {(() => { const soProjects = getSOProjects(so.id); return soProjects.length > 0 && (
                  <div className="border-t border-[#e8e8e5] px-3 py-2 pl-10">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Linked Projects</p>
                    {soProjects.map(prj => {
                      const prjPct = safePct(prj.progressPercentage || 0);
                      return (
                      <div key={prj.id} className="grid grid-cols-[16px_74px_1fr_42px] items-center gap-2 py-1">
                        <FolderKanban size={12} className="text-slate-400"/>
                        <span className="text-[8px] font-mono text-slate-600 uppercase truncate">{prj.reference}</span>
                        <span className="text-[10px] text-slate-700 truncate">{prj.title}</span>
                        <span className="text-[10px] font-black text-right" style={{color:pc(prjPct)}}>{prjPct}%</span>
                      </div>
                    );})}
                  </div>
                ); })()}

                {(so.kpis || []).length > 0 && (
                  <div className="border-t border-[#e8e8e5] px-3 py-2 pl-10">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-1.5">KPI Measures</p>
                    {so.kpis.map(kpi => {
                      const actual = kpi.actuals?.sort((a,b) => (b.quarter||0)-(a.quarter||0))[0];
                      const variance = actual?.variancePercentage;
                      return (
                        <div key={kpi.id} className="grid grid-cols-[16px_74px_1fr_120px_120px] items-center gap-2 py-1">
                          <BarChart3 size={12} className="text-slate-400"/>
                          <span className="text-[8px] font-mono text-slate-600 uppercase truncate">{kpi.reference}</span>
                          <span className="text-[10px] text-slate-700 truncate">{kpi.name}</span>
                          <span className="text-[9px] text-slate-500 truncate">Target: {kpi.annualTarget} {kpi.unit}</span>
                          {actual && <span className={`text-[10px] font-black text-right ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Q{actual.quarter}: {actual.actualValue} ({variance > 0 ? '+' : ''}{variance?.toFixed(1)}%)</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {(so.risks || []).length > 0 && (
                  <div className="border-t border-[#e8e8e5] px-3 py-2 pl-10">
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Linked Risks</p>
                    {so.risks.map(r => {
                      const score = r.residualLikelihood * r.residualImpact;
                      return (
                        <div key={r.id} className="grid grid-cols-[16px_74px_1fr_72px] items-center gap-2 py-1">
                          <AlertTriangle size={12} className="text-[#970c24]"/>
                          <span className="text-[8px] font-mono text-[#970c24] uppercase truncate">{r.reference}</span>
                          <span className="text-[10px] text-slate-700 truncate">{r.title}</span>
                          <span className="justify-self-end px-2 py-0.5 rounded-full text-[8px] font-black bg-red-50 text-[#970c24]">Score: {score}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="border-t border-[#e8e8e5] px-3 py-2 pl-10">
                  <button onClick={(e) => {e.stopPropagation(); openAddInit(so.id);}} className="text-[10px] text-[#970c24] font-black hover:underline flex items-center gap-1">+ Add Programme</button>
                </div>
              </div>
            )}
          </div>
        );})}
      </div>

      <Modal open={modal==='addSO'} onClose={()=>setModal(null)} title="Add Strategic Objective">
        <div className="space-y-4"><div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Title</label><input className={input} value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. Advance mineral beneficiation research" /></div><div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label><textarea className={input} rows={3} value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} /></div><div className="grid grid-cols-2 gap-3"><div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Financial Year</label><input type="number" className={input} value={form.financialYear||2026} onChange={e=>setForm({...form,financialYear:+e.target.value})} /></div><div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Sort Order</label><input type="number" className={input} value={form.sortOrder||''} onChange={e=>setForm({...form,sortOrder:+e.target.value})} /></div></div><p className="text-[10px] text-slate-400">Reference number will be auto-generated (e.g. SO-06)</p><div className="flex justify-end gap-2 pt-2"><button onClick={()=>setModal(null)} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200">Cancel</button><button onClick={saveSO} className="px-5 py-2.5 bg-[#c8102e] text-white rounded-xl text-sm font-semibold hover:bg-[#a00d24] shadow-sm">Create Objective</button></div></div>
      </Modal>
      <Modal open={modal==='addInit'} onClose={()=>setModal(null)} title="Add Programme / Programme"><div className="space-y-4"><div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Title</label><input className={input} value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} /></div><div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label><textarea className={input} rows={2} value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} /></div><p className="text-[10px] text-slate-400">Reference number will be auto-generated (e.g. PROG-05)</p><div className="flex justify-end gap-2 pt-2"><button onClick={()=>setModal(null)} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold">Cancel</button><button onClick={saveInit} className="px-5 py-2.5 bg-[#c8102e] text-white rounded-xl text-sm font-semibold shadow-sm">Create Programme</button></div></div></Modal>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

// ════════════════════════════════════════
// TAB 2: ALIGNMENT & RELATIONSHIPS (hierarchical traceability — unchanged)
// ════════════════════════════════════════
// ── V4: Alignment by Cluster & Division (C.1) ──
// Shows, per cluster, each division's business plan for the FY: existence,
// approval stage, and linked-objective count — organisational objectives
// cascading into cluster and divisional plans at a glance.
function ClusterDivisionAlignment({ objectives, divisionFilter }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { let live = true;
    Promise.all([clustersApi.getAll(), businessPlansApi.getAll()])
      .then(([c, p]) => { if (live) setData({ clusters: c.data || [], allPlans: p.data || [] }); })
      .catch(() => { if (live) setData({ clusters: [], allPlans: [] }); });
    return () => { live = false; };
  }, []);
  if (!data) return null;
  const { clusters, allPlans } = data;
  // Use the latest financial year that actually has plans (FY stored by start year, e.g. 2025 = FY 2025/26)
  const fy = allPlans.length ? Math.max(...allPlans.map(p => p.financialYear || 0)) : new Date().getFullYear();
  const plans = allPlans.filter(p => p.financialYear === fy);
  const soLinkCount = (plan) => (plan.strategicObjectives || plan.linkedObjectives || []).length || null;
  const stageChip = (status) => {
    const done = String(status || '').toLowerCase().includes('approv');
    const draft = String(status || '').toLowerCase().includes('draft');
    return <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${done ? 'bg-green-100 text-green-800' : draft ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-800'}`}>{status || '—'}</span>;
  };
  return (
    <div className={`${softPanel} p-5`}>
      <h3 className={tinyHeading}>Alignment by Cluster & Division — FY {fy}/{(fy+1)%100}</h3>
      <p className="text-xs text-slate-500 mt-2 mb-5">Organisational objectives ({objectives.length}) cascading into cluster and divisional business plans (C.1)</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {clusters.filter(cl => !divisionFilter || plans.some(p => p.divisionId === divisionFilter && (p.clusterId === cl.id || p.division?.clusterId === cl.id))).map(cl => {
          const clusterPlan = plans.find(p => !p.divisionId && p.clusterId === cl.id);
          const divPlans = plans.filter(p => p.divisionId && (p.clusterId === cl.id || p.division?.clusterId === cl.id)).filter(p => !divisionFilter || p.divisionId === divisionFilter);
          return (
            <div key={cl.id} className="rounded-xl border border-slate-300 overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center justify-between">
                <p className="text-xs font-black text-slate-800 uppercase tracking-wide">{cl.name}</p>
                {clusterPlan
                  ? <span className="flex items-center gap-2 text-[10px] text-slate-500">Cluster plan {stageChip(clusterPlan.status)}{soLinkCount(clusterPlan) != null && <span>{soLinkCount(clusterPlan)} objectives</span>}</span>
                  : <span className="text-[10px] text-amber-600 font-semibold">No cluster plan</span>}
              </div>
              <div className="divide-y divide-slate-100">
                {divPlans.length > 0 ? divPlans.map(p => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                    <span className="flex-1 text-slate-700 truncate">{p.division?.name || p.title}</span>
                    {soLinkCount(p) != null && <span className="text-[10px] text-slate-400">{soLinkCount(p)} objectives</span>}
                    {stageChip(p.status)}
                  </div>
                )) : <p className="px-4 py-2 text-[10px] text-slate-400">No divisional plans in this cluster for FY {fy}/{(fy+1)%100}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlignmentTab({ objectives, allProjects = [] }) {
  const [divisions, setDivisions] = React.useState([]);
  const [divisionFilter, setDivisionFilter] = React.useState('');
  React.useEffect(() => { divisionsApi.getAll().then(({ data }) => setDivisions(data || [])).catch(() => {}); }, []);
  // Helper: get all projects linked to an SO — from BOTH paths:
  // 1. Old FK: so.initiatives → init.projects
  // 2. New junction: project.strategicObjectives contains this SO
  const getProjectsForSO = (soId) => {
    try {
      const fromInitiatives = (objectives.find(o => o.id === soId)?.initiatives || []).flatMap(i => i.projects || []);
      const fromJunction = (allProjects || []).filter(p => {
        try { return (p.strategicObjectives || []).some(x => x.strategicObjectiveId === soId); } catch { return false; }
      });
      const seen = new Set(fromInitiatives.map(p => p.id));
      return [...fromInitiatives, ...fromJunction.filter(p => !seen.has(p.id))];
    } catch { return []; }
  };
  const filterByDivision = (arr, getDiv) => divisionFilter ? (arr || []).filter(x => getDiv(x) === divisionFilter) : (arr || []);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-2">
        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Filter by division</label>
        <select className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20" value={divisionFilter} onChange={e => setDivisionFilter(e.target.value)}>
          <option value="">All divisions</option>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <ClusterDivisionAlignment objectives={objectives} divisionFilter={divisionFilter} />
      <div className={`${softPanel} p-5`}>
        <h3 className={tinyHeading}>Strategy - Programme - Project - Risk Alignment Map</h3>
        <p className="text-xs text-slate-500 mt-2 mb-6">Full traceability from corporate strategy to operational execution (S.4 requirement)</p>
        <div className="space-y-5">
          {objectives.map(so => {
            const programmes = so.initiatives || [];
            const projects = filterByDivision(getProjectsForSO(so.id), p => p.divisionId);
            const risks = filterByDivision(so.risks || [], r => r.divisionId);
            if (divisionFilter && projects.length === 0 && risks.length === 0) return null;
            return (
              <div key={so.id} className="grid grid-cols-[1.1fr,20px,1.1fr,20px,1.1fr,20px,1.1fr] gap-3 items-stretch">
                <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 min-h-[78px]">
                  <span className="text-[9px] font-mono font-bold text-[#970c24]">{so.reference}</span>
                  <p className="text-xs font-semibold text-slate-800 mt-1 leading-tight">{so.title}</p>
                  <div className="flex items-center gap-2 mt-3"><div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-[#970c24]" style={{width:`${so.progressPercentage}%`}} /></div><span className="text-[10px] font-bold text-[#970c24]">{so.progressPercentage}%</span></div>
                </div>
                <div className="flex items-center justify-center text-slate-400">→</div>
                <div className="space-y-2">{programmes.length > 0 ? programmes.map(prog => (<div key={prog.id} className="rounded-md border border-slate-300 bg-white p-2"><span className="text-[9px] font-mono text-slate-600">{prog.reference}</span><p className="text-[11px] text-slate-700 leading-tight">{prog.title}</p></div>)) : <div className="rounded-md border border-dashed border-slate-300 bg-white p-2 text-center text-[10px] text-slate-400">No programmes</div>}</div>
                <div className="flex items-center justify-center text-slate-400">→</div>
                <div className="space-y-2">{projects.length > 0 ? projects.slice(0,3).map(p => (<div key={p.id} className="rounded-md border border-slate-300 bg-white p-2"><span className="text-[9px] font-mono text-[#970c24]">{p.reference}</span><p className="text-[11px] text-slate-700 leading-tight">{p.title}</p></div>)) : <div className="rounded-md border border-dashed border-slate-300 bg-white p-2 text-center text-[10px] text-slate-400">No projects</div>}{projects.length > 3 && <p className="text-[10px] text-slate-500 text-center">+{projects.length-3} more</p>}</div>
                <div className="flex items-center justify-center text-slate-400">→</div>
                <div className="space-y-2">{risks.length > 0 ? risks.slice(0,2).map(r => { const score = r.residualLikelihood * r.residualImpact; return (<div key={r.id} className="rounded-md border border-slate-300 bg-slate-100 p-2"><span className="text-[9px] font-mono text-slate-600">{r.reference}</span><p className="text-[11px] text-slate-700 leading-tight">{r.title}</p><span className="text-[9px] font-bold text-slate-700">Score: {score}</span></div>); }) : <div className="rounded-md border border-dashed border-slate-300 bg-white p-2 text-center text-[10px] text-slate-400">No risks linked</div>}</div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-6 pt-4 border-t border-slate-200">
          {[{l:'Strategic Objective',c:'bg-white border-[#970c24]'}, {l:'Programme',c:'bg-white border-slate-400'}, {l:'Project',c:'bg-white border-slate-400'}, {l:'Risk',c:'bg-slate-100 border-slate-400'}].map(x => (<span key={x.l} className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-700"><span className={`w-3 h-3 rounded border ${x.c}`} />{x.l}</span>))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// TAB 3: METRICS (KPI Performance — unchanged)
// ════════════════════════════════════════
function MetricsTab({ objectives }) {
  const allKpis = objectives.flatMap(o => (o.kpis || []).map(k => ({...k, soRef: o.reference, soTitle: o.title})));

  const getLatestActual = (kpi) => {
    const actuals = Array.isArray(kpi.actuals) ? [...kpi.actuals] : [];
    return actuals.sort((x, y) => (y.quarter || 0) - (x.quarter || 0))[0];
  };

  const formatActualDate = (a) => {
    const raw = a?.reportingDate || a?.captureDate || a?.createdAt || a?.updatedAt || a?.date;
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-GB');
  };

  const scorePill = (a) => {
    if (!a) return <span className="inline-flex min-w-[56px] justify-center rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">No Data</span>;
    const approved = a.approvalStatus === 'Approved' || a.approvalStatus === 1;
    return approved
      ? <span className="inline-flex min-w-[56px] justify-center rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Approved</span>
      : <span className="inline-flex min-w-[56px] justify-center rounded-full border border-[#970c24]/35 bg-[#970c24]/10 px-2 py-0.5 text-[10px] font-bold text-[#970c24]">Pending</span>;
  };

  const variancePill = (v) => {
    if (v == null) return <span className="text-[10px] text-slate-400">—</span>;
    const positive = v >= 0;
    return (
      <span className={`inline-flex min-w-[58px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-black ${positive ? 'border-emerald-300 bg-emerald-100 text-emerald-700' : 'border-[#970c24]/30 bg-[#970c24]/10 text-[#970c24]'}`}>
        {v > 0 ? '+' : ''}{v.toFixed(1)}% {positive ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        {(() => {
          const total = allKpis.length;
          const withActuals = allKpis.filter(k => k.actuals?.length > 0);
          const onTarget = withActuals.filter(k => (k.actuals[0]?.variancePercentage || 0) >= 0).length;
          const belowTarget = withActuals.filter(k => (k.actuals[0]?.variancePercentage || 0) < 0).length;
          const pending = total - withActuals.length;
          return [
            {l:'Total IDs',v:total},
            {l:'On / Above Target',v:onTarget},
            {l:'Below Target',v:belowTarget},
            {l:'Pending Capture',v:pending},
          ].map(s => (
            <div key={s.l} className="rounded-md border-[3px] border-[#2f3438] bg-white/95 px-4 py-3 shadow-[0_3px_0_rgba(0,0,0,0.22),0_12px_22px_rgba(15,23,42,0.16)]">
              <p className="text-[12px] font-black uppercase leading-none tracking-[-0.02em] text-slate-900">{s.l}</p>
              <div className="mt-2 flex items-end justify-between gap-3">
                <p className="text-[27px] font-black leading-none tracking-tight text-black">{s.v}</p>
                <span className="pb-0.5 text-[10px] leading-none text-black">Percentage goal</span>
              </div>
            </div>
          ));
        })()}
      </div>

      <div className="overflow-hidden rounded-sm border border-[#c8cdd1] bg-white shadow-[0_12px_28px_rgba(15,23,42,0.16)]">
        <div className="bg-[#f4f5f6] px-2 py-2">
          <h3 className="text-[13px] font-black uppercase leading-none tracking-[-0.03em] text-black">APP &amp; Operational KPI Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] border-collapse text-[11px] leading-tight">
            <thead>
              <tr className="bg-[#d9dde0] text-[#111]">
                <th className="w-[110px] px-3 py-2 text-left text-[9px] font-bold uppercase">Ref</th>
                <th className="px-3 py-2 text-left text-[9px] font-bold uppercase">KPI Measure</th>
                <th className="w-[120px] px-3 py-2 text-left text-[9px] font-bold uppercase">Objective</th>
                <th className="w-[95px] px-3 py-2 text-center text-[9px] font-bold uppercase">Type</th>
                <th className="w-[130px] px-3 py-2 text-right text-[9px] font-bold uppercase">Annual Target</th>
                <th className="w-[120px] px-3 py-2 text-right text-[9px] font-bold uppercase">Q1 Target</th>
                <th className="w-[120px] px-3 py-2 text-right text-[9px] font-bold uppercase">Q1 Actual</th>
                <th className="w-[120px] px-3 py-2 text-right text-[9px] font-bold uppercase">Variance</th>
                <th className="w-[95px] px-3 py-2 text-center text-[9px] font-bold uppercase">Score</th>
                <th className="w-[92px] px-3 py-2 text-right text-[9px] font-bold uppercase"></th>
              </tr>
            </thead>
            <tbody>
              {allKpis.map((kpi, idx) => {
                const a = getLatestActual(kpi);
                const v = a?.variancePercentage;
                const rowTint = v != null && v < 0 ? 'bg-[#f5d3d3]' : v != null && v >= 0 ? 'bg-white' : (idx % 2 === 1 ? 'bg-[#edf0f2]' : 'bg-white');
                return (
                  <tr key={kpi.id} className={`${rowTint} border-b border-white hover:bg-[#e9edf0] transition-colors`}>
                    <td className="px-3 py-[7px] align-middle"><span className="font-mono text-[10px] font-black text-black">{kpi.reference}</span></td>
                    <td className="max-w-[360px] px-3 py-[7px] align-middle text-[10px] font-semibold text-black">{kpi.name}</td>
                    <td className="px-3 py-[7px] align-middle text-[10px] font-semibold text-slate-700">{kpi.soRef}</td>
                    <td className="px-3 py-[7px] text-center align-middle">
                      {kpi.isAppIndicator
                        ? <span className="inline-flex min-w-[36px] justify-center rounded-full border border-emerald-400 bg-emerald-100 px-2 py-0.5 text-[9px] font-black text-emerald-800">APP</span>
                        : <span className="inline-flex min-w-[36px] justify-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[9px] font-black text-slate-700">OPS</span>}
                    </td>
                    <td className="px-3 py-[7px] text-right align-middle font-mono text-[10px] font-black text-black">{kpi.annualTarget} {kpi.unit}</td>
                    <td className="px-3 py-[7px] text-right align-middle font-mono text-[10px] font-semibold text-black">{a?.targetValue ?? kpi.q1Target ?? '—'}</td>
                    <td className="px-3 py-[7px] text-right align-middle font-mono text-[10px] font-black text-black">{a?.actualValue ?? '—'}</td>
                    <td className="px-3 py-[7px] text-right align-middle">{variancePill(v)}</td>
                    <td className="px-3 py-[7px] text-center align-middle">{scorePill(a)}</td>
                    <td className="px-3 py-[7px] text-right align-middle font-mono text-[10px] font-semibold text-black">{formatActualDate(a)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// TAB 4: STRATEGIC ALIGNMENT (V3.7)
// Reconciles top-down company target weights against bottom-up divisional plans.
// Three sections: target-vs-actual bars, contribution heatmap, alignment scores.
// ════════════════════════════════════════
function StrategicAlignmentTab({ fy }) {
  const [data, setData] = useState(EMPTY_ALIGNMENT);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    strategicAlignmentApi.getDashboard(fy)
      .then(r => setData(r.data || EMPTY_ALIGNMENT))
      .catch(() => setData(EMPTY_ALIGNMENT))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [fy]);

  const sortedScores = useMemo(() => [...data.matrix].sort((a, b) => b.alignmentScore - a.alignmentScore), [data.matrix]);
  const maxObjVal = useMemo(() => Math.max(32, ...data.objectives.flatMap(o => [o.targetWeight, o.actualWeight])), [data.objectives]);

  if (loading) return <div className="text-center py-12 text-slate-400 text-sm">Loading alignment dashboard…</div>;

  return (
    <div className="space-y-5">
      {/* ─── Summary cards ─── */}
      <div className={`${softPanel} overflow-hidden`}>
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className={tinyHeading}>Strategic Alignment Overview</h3>
          <span className="text-xs text-slate-400">FY {fy-1}/{fy%100}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 px-4 pb-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="h-7 bg-[#22272b]" />
            <div className="p-4">
              <p className="text-slate-700 text-xs font-bold uppercase">Divisions</p>
              <div className="flex items-end gap-2 mt-1"><span className="text-4xl leading-none font-black text-slate-900">{data.summary.divisionCount}</span><span className="text-[10px] text-slate-500 mb-1">{data.summary.divisionsWithCompletePlans} complete</span></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="h-7 bg-slate-400" />
            <div className="p-4">
              <p className="text-slate-700 text-xs font-bold uppercase">Objectives</p>
              <div className="flex items-end gap-2 mt-1"><span className="text-4xl leading-none font-black text-slate-900">{data.summary.objectiveCount}</span><span className="text-[10px] text-slate-500 mb-1">Σ targets {data.summary.companyTargetSumsTo}%</span></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="h-7" style={{background: aColor(data.summary.averageAlignment)}} />
            <div className="p-4">
              <p className="text-slate-700 text-xs font-bold uppercase">Avg alignment</p>
              <div className="flex items-end gap-2 mt-1"><span className="text-4xl leading-none font-black" style={{color: aColor(data.summary.averageAlignment)}}>{data.summary.averageAlignment}%</span><span className="text-[10px] text-slate-500 mb-1">{aBand(data.summary.averageAlignment)}</span></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div className="h-7 bg-[#970c24]" />
            <div className="p-4">
              <p className="text-slate-700 text-xs font-bold uppercase">Largest variance</p>
              <div className="flex items-end gap-2 mt-1">
                <span className="text-4xl leading-none font-black" style={{color: vColor(data.summary.largestVarianceValue || 0)}}>
                  {data.summary.largestVarianceValue > 0 ? '+' : ''}{(data.summary.largestVarianceValue || 0).toFixed(1)}%
                </span>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 truncate" title={data.summary.largestVarianceObjective}>{data.summary.largestVarianceObjective}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Section 1: Target vs Actual ─── */}
      <div className={`${softPanel} p-5`}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className={tinyHeading}>Strategic Objective Alignment — Target vs Actual</h3>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background: '#10b981'}}/>Aligned</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background: '#f59e0b'}}/>Over</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background: '#ef4444'}}/>Under</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-0.5 h-3 bg-slate-800"/>Target</span>
          </div>
        </div>
        {data.objectives.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-xs">
            <AlertTriangle size={28} className="mx-auto mb-2 opacity-50"/>
            <p>No strategic objectives defined for FY {fy-1}/{fy%100}.</p>
            <p className="mt-1 text-[10px]">Add objectives on the Planner tab first, then set weightings.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.objectives.map(o => {
              const actualPct = (o.actualWeight / maxObjVal) * 100;
              const targetPct = (o.targetWeight / maxObjVal) * 100;
              return (
                <div key={o.id} className="grid grid-cols-[180px,1fr,160px] items-center gap-3 py-1.5 hover:bg-slate-50/70 rounded-lg px-2 -mx-2">
                  <div className="text-right pr-1">
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-[9px] font-mono font-bold text-[#970c24] bg-red-50 px-1.5 py-0.5 rounded">{o.reference}</span>
                    </div>
                    <div className="text-xs text-slate-700 truncate mt-0.5" title={o.title}>{o.title}</div>
                  </div>
                  <div className="relative h-4 bg-slate-100 rounded-lg">
                    <div className="absolute top-0 left-0 h-full rounded-lg opacity-85" style={{ width: `${actualPct}%`, background: vColor(o.variance) }}/>
                    <div className="absolute -top-1.5 -bottom-1.5 w-0.5 bg-slate-800" style={{ left: `${targetPct}%` }} title={`Target ${o.targetWeight}%`}/>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-[10px] text-slate-500">{o.actualWeight}% / {o.targetWeight}%</span>
                    <span className="text-xs font-black w-12 text-right" style={{color: vColor(o.variance)}}>
                      {o.variance > 0 ? '+' : ''}{o.variance.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Section 2: Contribution matrix heatmap ─── */}
      <div className={`${softPanel} p-5`}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className={tinyHeading}>Division Contribution Matrix — % of Effort by Objective</h3>
          <div className="flex items-center gap-2 text-[10px] text-slate-500">
            <span>Low</span>
            <div className="flex h-2 rounded overflow-hidden w-28">
              {[0.12, 0.3, 0.5, 0.7, 0.92].map((op, i) => (
                <div key={i} className="flex-1" style={{ background: `rgba(151, 12, 36, ${op})` }}/>
              ))}
            </div>
            <span>High</span>
          </div>
        </div>
        {data.matrix.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-xs">
            <AlertTriangle size={28} className="mx-auto mb-2 opacity-50"/>
            <p>No divisions or contributions to display.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate" style={{ borderSpacing: '4px' }}>
              <thead>
                <tr>
                  <th className="text-left pr-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-44">Division</th>
                  {data.objectives.map(o => (
                    <th key={o.id} className="text-center text-[10px] font-bold text-slate-600 px-1 py-2" title={o.title}>
                      <div className="truncate max-w-[80px] mx-auto"><span className="font-mono text-[#970c24]">{o.reference}</span></div>
                      <div className="text-[9px] font-normal text-slate-400 mt-0.5">tgt {o.targetWeight}%</div>
                    </th>
                  ))}
                  <th className="text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-16">Total</th>
                  <th className="w-10"/>
                </tr>
              </thead>
              <tbody>
                {data.matrix.map(row => (
                  <tr key={row.divisionId} className="group">
                    <td className="text-right pr-2 py-1.5">
                      <div className="text-xs text-slate-800 font-semibold truncate" title={row.divisionName}>{row.divisionName}</div>
                      {row.divisionCode && <div className="text-[9px] text-slate-400 font-mono">{row.divisionCode}</div>}
                    </td>
                    {row.cells.map(c => (
                      <td key={c.objectiveId} className="text-center">
                        <div
                          className="h-9 rounded-lg flex items-center justify-center font-bold text-[11px] cursor-help transition-transform hover:scale-105"
                          style={cellStyle(c.weightPercent)}
                          title={`${row.divisionName} → ${c.objectiveTitle}: ${c.weightPercent}%${c.rationale ? `\n${c.rationale}` : ''}`}
                        >
                          {c.weightPercent || ''}
                        </div>
                      </td>
                    ))}
                    <td className="text-center">
                      <span className={`inline-block px-2 py-1 rounded-lg text-[10px] font-bold ${row.rowComplete ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {row.rowTotal}%
                      </span>
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => setEditing(row)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-[#970c24] hover:bg-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit allocation"
                      >
                        <Edit3 size={13}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Section 3: Division alignment scores ─── */}
      <div className={`${softPanel} p-5`}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className={tinyHeading}>Division Alignment Score — Distribution Match Against Company Priorities</h3>
          <span className="text-[10px] text-slate-400 font-mono">1 − ½Σ|D − W|</span>
        </div>
        {sortedScores.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-xs">
            <AlertTriangle size={28} className="mx-auto mb-2 opacity-50"/>
            <p>No alignment scores to display.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {sortedScores.map(row => (
              <div key={row.divisionId} className="grid grid-cols-[180px,1fr,140px] items-center gap-3 py-1.5 hover:bg-slate-50/70 rounded-lg px-2 -mx-2">
                <div className="text-right text-xs text-slate-800 font-semibold truncate" title={row.divisionName}>{row.divisionName}</div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full opacity-85" style={{ width: `${row.alignmentScore}%`, background: aColor(row.alignmentScore) }}/>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{row.alignmentBand}</span>
                  <span className="text-xs font-black w-10 text-right" style={{color: aColor(row.alignmentScore)}}>{row.alignmentScore}%</span>
                </div>
              </div>
            ))}
            <div className="pt-3 mt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500 flex-wrap gap-2">
              <span className="inline-flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background: '#10b981'}}/>Strong (≥90%)</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background: '#f59e0b'}}/>Moderate (85–90%)</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background: '#ef4444'}}/>Weak (&lt;85%)</span>
              </span>
              <span>Lower scores = plan diverges from company target weights</span>
            </div>
          </div>
        )}
      </div>

      {/* ─── Edit modal (reuses page Modal component) ─── */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Contribution Allocation" width="max-w-2xl">
        {editing && (
          <EditContributionsForm
            division={editing}
            objectives={data.objectives}
            fy={fy}
            onSaved={() => { setEditing(null); load(); }}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// ── Allocation editor (used inside the page Modal) ─────────────────────────
function EditContributionsForm({ division, objectives, fy, onSaved, onCancel }) {
  const [weights, setWeights] = useState({});
  const [rationales, setRationales] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!division) return;
    const w = {}, r = {};
    division.cells.forEach(c => { w[c.objectiveId] = c.weightPercent; r[c.objectiveId] = c.rationale || ''; });
    setWeights(w); setRationales(r); setError('');
  }, [division]);

  const total = useMemo(() => Object.values(weights).reduce((s, v) => s + (+v || 0), 0), [weights]);
  const isValid = Math.abs(total - 100) < 0.5;

  const save = async () => {
    if (!isValid) { setError(`Weights must sum to 100. Currently ${total.toFixed(2)}%.`); return; }
    setSaving(true); setError('');
    try {
      await strategicAlignmentApi.setContributions({
        divisionId: division.divisionId,
        financialYear: fy,
        items: objectives.map(o => ({
          strategicObjectiveId: o.id,
          weightPercent: +weights[o.id] || 0,
          rationale: rationales[o.id] || null
        }))
      });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Set what percent of <span className="font-bold text-slate-800">{division.divisionName}</span>'s annual effort is allocated to each strategic objective. Total must equal 100%.
      </p>
      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        {objectives.map(o => {
          const w = weights[o.id] ?? 0;
          const v = (+w || 0) - o.targetWeight;
          return (
            <div key={o.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono font-bold text-[#970c24] bg-red-50 px-1.5 py-0.5 rounded">{o.reference}</span>
                    <span className="text-xs font-semibold text-slate-800 truncate">{o.title}</span>
                  </div>
                  <div className="text-[10px] text-slate-500">Company target: <span className="font-bold text-slate-700">{o.targetWeight}%</span></div>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0" max="100" step="0.1"
                    value={w}
                    onChange={e => setWeights({...weights, [o.id]: e.target.value})}
                    className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-right text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e]"
                  />
                  <span className="text-xs text-slate-400">%</span>
                  {Math.abs(v) > 0.5 && (
                    <span className="text-[11px] font-bold w-12 text-right" style={{ color: vColor(v) }}>
                      {v > 0 ? '+' : ''}{v.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
              <input
                type="text"
                placeholder="Rationale (optional) — e.g. flagship research programme, 4 KPIs in scope"
                value={rationales[o.id] || ''}
                onChange={e => setRationales({...rationales, [o.id]: e.target.value})}
                className="mt-2 w-full px-2 py-1.5 border border-slate-100 rounded-lg text-[11px] text-slate-600 placeholder-slate-300 focus:outline-none focus:border-[#c8102e]"
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <span className={tinyHeading}>Total</span>
          <span className="text-lg font-black" style={{color: isValid ? '#10b981' : '#ef4444'}}>{total.toFixed(2)}%</span>
          {isValid && <CheckCircle2 size={14} className="text-emerald-500"/>}
          {!isValid && <span className="text-[10px] text-red-600">Must equal 100</span>}
        </div>
        <div className="flex items-center gap-2">
          {error && <span className="text-[11px] text-red-600 mr-1">{error}</span>}
          <button onClick={onCancel} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold">Cancel</button>
          <button
            onClick={save}
            disabled={!isValid || saving}
            className="px-5 py-2.5 bg-[#c8102e] text-white rounded-xl text-sm font-semibold hover:bg-[#a00d24] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {saving ? 'Saving…' : 'Save Allocation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// TAB 5: BUSINESS PLANS (unchanged — kept in file even though not in TABS)
// ════════════════════════════════════════
function BusinessPlansTab({ fy = 2026 }) {
  const [plans, setPlans] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { load(); }, []);
  const load = () => businessPlansApi.getAll({financialYear:fy}).then(r=>setPlans(r.data)).catch(()=>{});
  const updateStatus = async (id, status) => { try { await businessPlansApi.updateStatus(id, status); load(); } catch {} };

  const stages = ['Draft','InProgress','Active','OnHold','Completed','Closed'];
  const si = s => stages.indexOf(s);
  const pct = s => ((si(s)+1)/stages.length)*100;

  const openAdd = () => { setForm({title:'',financialYear:fy,status:'Draft'}); setModal('add'); };
  const handleSave = async () => { try { await businessPlansApi.create(form); setModal(null); load(); } catch {} };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-6 gap-2">
        {stages.map(s => {
          const count = plans.filter(p => p.status === s).length;
          return (
            <div key={s} className="rounded-xl p-3 text-center text-white" style={{backgroundColor: stageColor(s)}}>
              <p className="text-xl font-black">{count}</p>
              <p className="text-[9px] font-semibold uppercase opacity-90">{STAGES[s]}</p>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Divisional Business Plans — FY 2025/26</h3>
        <button onClick={openAdd} className="px-4 py-2 bg-[#c8102e] text-white rounded-xl text-sm font-semibold flex items-center gap-2 shadow-sm"><span className="text-lg leading-none">+</span> Add Plan</button>
      </div>

      <div className="space-y-3">
        {plans.map(plan => (
          <div key={plan.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: stageColor(plan.status) + '20'}}>
                <FileText size={16} className="text-slate-400"/>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">{plan.title}</p>
                <p className="text-xs text-slate-400">{plan.division?.name || plan.cluster?.name || 'Unassigned'} · FY {plan.financialYear}</p>
              </div>
              <select className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs bg-white font-semibold" value={plan.status} onChange={e => updateStatus(plan.id, e.target.value)}>
                {stages.map(s => <option key={s} value={s}>{STAGES[s]}</option>)}
              </select>
            </div>

            {/* Stage tracker */}
            <div className="flex items-center gap-1 mb-1.5">
              {stages.map((s, i) => {
                const reached = si(plan.status) >= i;
                const current = plan.status === s;
                return (
                  <React.Fragment key={s}>
                    <div className={`flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold transition-all ${reached ? 'text-white shadow-sm' : 'bg-slate-200 text-slate-400'} ${current ? 'ring-2 ring-offset-2 ring-[#c8102e] scale-110' : ''}`}
                      style={reached ? {backgroundColor: stageColor(s)} : {}}>
                      {reached ? '' : i+1}
                    </div>
                    {i < stages.length-1 && <div className={`flex-1 h-1 rounded-full transition-all`} style={{backgroundColor: reached ? stageColor(s) : '#e2e8f0'}} />}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex justify-between mb-3">
              {stages.map(s => <span key={s} className="text-[8px] text-slate-400 text-center" style={{width:`${100/stages.length}%`}}>{STAGES[s]}</span>)}
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-[#c8102e] transition-all" style={{width:`${pct(plan.status)}%`}} />
            </div>
          </div>
        ))}
      </div>

      <Modal open={modal==='add'} onClose={()=>setModal(null)} title="Add Business Plan">
        <div className="space-y-4">
          <div><label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Title</label><input className={input} value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g. AMD Division Business Plan 2025/26" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Description</label><textarea className={input} rows={2} value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModal(null)} className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold">Cancel</button>
            <button onClick={handleSave} className="px-5 py-2.5 bg-[#c8102e] text-white rounded-xl text-sm font-semibold shadow-sm">Create Plan</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════
const TABS = [
  { key: 'planner', label: 'Planner', Icon: FileText },
  { key: 'alignment', label: 'Alignment & Relationships', Icon: Link2 },
  { key: 'metrics', label: 'Metrics', Icon: BarChart3 },
  { key: 'strategic-alignment', label: 'Strategic Alignment', Icon: Sliders },
];

export default function StrategyPlanningPage() {
  const [tab, setTab] = useState('planner');
  const [objectives, setObjectives] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [fy, setFy] = useState(2026);
  const { user } = useAuth();

  const load = () => {
    objectivesApi.getAll(fy).then(r => setObjectives(r.data)).catch(() => {});
    projectsApi.getAll({}).then(r => setAllProjects(r.data || [])).catch(() => setAllProjects([]));
  };
  useEffect(() => { load(); }, [fy]);

  return (
    <div className="min-h-screen bg-[#e7e7e4] -m-6 p-3 space-y-3 text-slate-900">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-black text-[18px] leading-none text-slate-900 tracking-tight">Strategy &amp; Business Planning</h1>
          <p className="text-slate-600 text-[10px] mt-1.5">FY {fy-1}/{fy%100} · Corporate Strategy → Divisional Plans → Programmes → Projects</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-8 px-2 border border-slate-300 rounded bg-[#eeeeec] text-[10px] text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#970c24]/40" value={fy} onChange={e=>{setFy(+e.target.value);}}>
            <option value={2025}>FY 24/25</option><option value={2026}>FY 25/26</option><option value={2027}>FY 26/27</option>
          </select>
          <span className="text-[9px] font-mono bg-[#d7d7d4] text-slate-600 px-2 py-1.5 rounded">Module S.1–S.5</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-md bg-[#d3d3d0] border border-[#c7c7c4] p-1 grid grid-cols-4 gap-1 shadow-sm">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded text-[10px] font-bold transition-all
              ${tab === t.key ? 'bg-[#970c24] text-white shadow-sm' : 'text-slate-700 hover:text-slate-900 hover:bg-[#eeeeec]'}`}>
            {t.Icon && <t.Icon size={12}/>}
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'planner' && <PlannerTab objectives={objectives} allProjects={allProjects} fy={fy} onRefresh={load} />}
      {tab === 'alignment' && <AlignmentTab objectives={objectives} allProjects={allProjects} />}
      {tab === 'metrics' && <MetricsTab objectives={objectives} />}
      {tab === 'strategic-alignment' && <StrategicAlignmentTab fy={fy} />}
    </div>
  );
}
